import { z } from "zod";
import { type AppointmentView, localDateTimeSchema, oneService, visitServiceSchema } from "./booking";

// ── Addresses (mobile visits) ──

export interface ResolvedAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  label: string;
}

export const mobileAddressSchema = z.object({
  line1: z.string().trim().min(1).max(120),
  line2: z.string().trim().max(120).default(""),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(2).max(40),
  postalCode: z.string().trim().min(3).max(12),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});
export type MobileAddress = z.infer<typeof mobileAddressSchema>;

/** Is this address inside the service area, and what does the trip cost? */
export interface AddressCheck {
  /** False only when the distance can't be checked at all. */
  ok: boolean;
  /** Outside the service area: the client may still send the request (it may be declined). */
  outside: boolean;
  /** Staff only: miles from the nearest center, and which one. Clients get null. */
  miles: number | null;
  from: string | null;
  travelFeeCents: number;
  /** What to tell the client (outside the area, or can't check). */
  message: string | null;
}

export const mapUrl = (a: { latitude: number | null; longitude: number | null; line1: string; city: string; state: string; postalCode: string }) =>
  a.latitude != null && a.longitude != null
    ? `https://maps.google.com/?q=${a.latitude},${a.longitude}`
    : `https://maps.google.com/?q=${encodeURIComponent(`${a.line1}, ${a.city}, ${a.state} ${a.postalCode}`)}`;

// ── Mobile requests (staff) ──

export const acceptMobileSchema = z.object({
  /** Managers may assign or change the nurse; nurses accept for themselves. */
  staffId: z.string().uuid().nullable().default(null),
  /** The trip's total travel fee; null keeps the service-area fee. */
  travelFeeCents: z.number().int().min(0).max(100_000).nullable().default(null),
  /** The client agreed to the travel fee before it was entered. */
  travelFeeApproved: z.boolean().default(false),
});

export const declineMobileSchema = z.object({ reason: z.string().trim().min(1, "Give a reason").max(300) });

export interface MobileRequestView {
  bookingId: string;
  reference: string;
  status: "pending" | "requested" | "confirmed" | "declined" | "expired";
  requestedAt: string;
  start: string;
  end: string;
  address: string;
  mapUrl: string;
  locationType: string;
  distanceMiles: number | null;
  /** The service-area center the distance is from (e.g. "Home"). */
  distanceFrom: string | null;
  /** Outside the service area: the client sent it knowing it may be declined. */
  outsideArea: boolean;
  travelFeeCents: number;
  acknowledgment: string | null;
  booker: { id: string; name: string; mobile: string | null; returning: boolean };
  guests: { appointmentId: string; clientId: string; name: string; age: number | null; serviceName: string; addOns: string[]; priceCents: number }[];
  totalCents: number;
  notes: string;
  staffId: string | null;
  staffName: string | null;
  /** Nurses who could take it (free and set for mobile). */
  candidates: { id: string; name: string }[];
  alerts: { level: "nurse" | "manager" | "owner"; name: string; at: string }[];
  decidedBy: string | null;
  declineReason: string | null;
  /** Can the signed-in staff member accept it (offered to them, or a manager)? */
  canAccept: boolean;
}

// ── Events ──

/**
 * Event pages live at /<event-slug>, beside the app's own pages, so these
 * first parts of a path can't be an event's link name.
 */
export const RESERVED_EVENT_SLUGS = [
  "account", "api", "assets", "audit", "availability", "book", "calendar", "catalog", "charts", "checkout", "clients", "communication", "confirm", "device", "e",
  "event", "events", "forms", "front-desk", "inventory", "join", "kiosk", "login", "logout", "mobile-requests", "note", "pay", "payments", "payouts",
  "pending-charts", "pr-charts", "records", "reports", "roles", "settings", "staff", "today", "v1", "visits", "voicemail",
];
const slug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/, "Use 3–40 lowercase letters, numbers, and dashes")
  .refine((v) => !RESERVED_EVENT_SLUGS.includes(v), "That link name is used by the app; pick another");
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a color like #C51A6A");

/** Where the hero photo meets the page (and the footer). */
export const HERO_EDGES = ["line", "pattern", "fade"] as const;
export type HeroEdge = (typeof HERO_EDGES)[number];

/** Fonts the event page can use without uploading one (bundled with the app). */
export const EVENT_FONTS = ["Marcellus", "Manrope", "Cormorant Garamond", "Playfair Display", "Montserrat", "Lato"] as const;
const fontChoice = z.object({ family: z.string().trim().min(1).max(60), key: z.string().max(500).nullable().default(null) });
export type EventFont = z.infer<typeof fontChoice>;
/**
 * Hero photo: cropped to 2:1 (wide). Under minWidth it can't look sharp on a computer (refused);
 * under goodWidth it may look a little soft on large screens (allowed, with a note).
 */
export const HERO_PHOTO = { ratio: 2, minWidth: 1200, goodWidth: 1600, maxWidth: 4000 };
/** What can be uploaded for an event page. */
export const EVENT_ASSETS = ["logo", "hero", "band", "font"] as const;
export type EventAsset = (typeof EVENT_ASSETS)[number];

export const eventInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug,
  host: z.string().trim().max(120).default(""),
  venueName: z.string().trim().max(120).default(""),
  venueAddress: z
    .object({ line1: z.string().trim().max(120), line2: z.string().trim().max(120).default(""), city: z.string().trim().max(80), state: z.string().trim().max(40), postalCode: z.string().trim().max(12) })
    .nullable()
    .default(null),
  intro: z.string().trim().max(2000).default(""),
  staffNotes: z.string().trim().max(2000).default(""),
  logoKey: z.string().max(500).nullable().default(null),
  primaryColor: hexColor.nullable().default(null),
  accentColor: hexColor.nullable().default(null),
  heroImageKey: z.string().max(500).nullable().default(null),
  /** Percent across and down: the part of the photo kept in view on narrow screens. */
  heroFocus: z.object({ x: z.number().min(0).max(100), y: z.number().min(0).max(100) }).nullable().default(null),
  bandImageKey: z.string().max(500).nullable().default(null),
  heroEdge: z.enum(HERO_EDGES).default("line"),
  edgeColor: hexColor.nullable().default(null),
  heroTop: z.string().trim().max(80).default(""),
  cityLine: z.string().trim().max(80).default(""),
  headline: z.string().trim().max(120).default(""),
  addOnsNote: z.string().trim().max(200).default(""),
  headingFont: fontChoice.nullable().default(null),
  bodyFont: fontChoice.nullable().default(null),
  backgroundColor: hexColor.nullable().default(null),
  cardColor: hexColor.nullable().default(null),
  textColor: hexColor.nullable().default(null),
  unlisted: z.boolean().default(true),
  bookingMode: z.enum(["slots", "walk_ups", "both"]).default("both"),
  slotMinutes: z.number().int().min(5).max(240).default(15),
  maxGuests: z.number().int().min(1).max(20).default(4),
  depositPercent: z.number().int().min(0).max(100).nullable().default(null),
  questions: z.array(z.object({ label: z.string().trim().min(1).max(120), required: z.boolean().default(false) })).max(5).default([]),
});
export type EventInput = z.infer<typeof eventInputSchema>;

export const eventMenuSchema = z.object({
  items: z
    .array(
      z.object({
        catalogItemId: z.string().uuid(),
        priceCents: z.number().int().min(0).max(10_000_000),
        name: z.string().trim().max(80).nullable().default(null),
        description: z.string().trim().max(1000).nullable().default(null),
        groupLabel: z.string().trim().max(40).nullable().default(null),
        /** Services: a drip, an extra (e.g. recovery boots) booked with or without a drip, or an add-on (e.g. a shot) only with a drip. */
        role: z.enum(["drip", "extra", "addon"]).default("drip"),
      }),
    )
    .max(100),
});

export const occurrenceInputSchema = z.object({
  start: localDateTimeSchema,
  end: localDateTimeSchema,
  chairCount: z.number().int().min(0).max(100),
  title: z.string().trim().max(80).default(""),
  subtitle: z.string().trim().max(80).default(""),
  staffIds: z.array(z.string().uuid()).max(50).default([]),
  notes: z.string().trim().max(500).default(""),
  /** Also add the same date and times every week until this date (new dates only). */
  repeatWeeklyUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
});

export interface EventOccurrenceView {
  id: string;
  start: string;
  end: string;
  chairCount: number;
  title: string;
  subtitle: string;
  staff: { id: string; name: string }[];
  notes: string;
  cancelled: boolean;
  booked: number;
}

export interface EventMenuItemView {
  catalogItemId: string;
  type: "service" | "add_on";
  catalogName: string;
  name: string | null;
  description: string | null;
  groupLabel: string | null;
  role: "drip" | "extra" | "addon";
  /** The catalog category (add-ons are listed under it unless groupLabel is set). */
  categoryName: string;
  priceCents: number;
  catalogPriceCents: number;
}

export interface EventSummary {
  id: string;
  name: string;
  slug: string;
  host: string;
  status: "active" | "archived";
  nextDate: string | null;
  dates: number;
}

export interface EventDetail extends EventInput {
  id: string;
  status: "active" | "archived";
  logoUrl: string | null;
  heroUrl: string | null;
  bandUrl: string | null;
  link: string;
  /** What's still needed before the page can take bookings. */
  missing: string[];
  occurrences: EventOccurrenceView[];
  menu: EventMenuItemView[];
}

/** The public event page. */
export interface OnlineEvent {
  id: string;
  name: string;
  host: string;
  venue: string;
  intro: string;
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  unlisted: boolean;
  bookingMode: "slots" | "walk_ups" | "both";
  maxGuests: number;
  depositPercent: number | null;
  questions: { label: string; required: boolean }[];
  /** Upcoming dates that take bookings. */
  occurrences: { id: string; start: string; end: string }[];
  services: { id: string; name: string; description: string; priceCents: number; minutes: number; includedAddOnIds: string[]; addOnIds: string[]; onePerVisit: boolean; medicationIds: string[] }[];
  addOns: { id: string; name: string; description: string; priceCents: number; minutes: number; medicationIds: string[] }[];
}

export interface CheckInView {
  occurrence: EventOccurrenceView & { eventId: string; eventName: string };
  appointments: AppointmentView[];
}

export const walkUpSchema = z.preprocess(oneService, z.object({
  client: z.union([
    z.object({ id: z.string().uuid() }),
    z.object({
      firstName: z.string().trim().min(1).max(80),
      lastName: z.string().trim().min(1).max(80),
      dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      mobile: z.string().trim().max(30).nullable().default(null),
      /** Required for adults (checked at check-in); minors can leave it empty. */
      email: z.string().trim().toLowerCase().max(254).nullable().default(null),
    }),
  ]),
  services: z.array(visitServiceSchema).min(1, "Pick a service").max(8),
  /** Why it's against the visit rules (two drips, or a medication twice), if it is. */
  servicesReason: z.string().trim().max(300).default(""),
  /** Why something not on the event's menu is given (staff), logged. */
  offMenuReason: z.string().trim().max(300).default(""),
}));
